import type { UpdateSentiment } from '../types';

const POSITIVE = [
  'growth',
  'profitable',
  'revenue up',
  'won',
  'launched',
  'strong',
  'improved',
  'signed',
  'closed',
];

const NEGATIVE = [
  'loss',
  'down',
  'delayed',
  'churn',
  'failed',
  'cash',
  'runway',
  'problem',
  'risk',
];

function normalize(text: string): string {
  return text.toLowerCase();
}

function countHits(text: string, words: string[]): number {
  const n = normalize(text);
  return words.reduce((acc, w) => (n.includes(w.toLowerCase()) ? acc + 1 : acc), 0);
}

export function detectSentiment(text: string): UpdateSentiment {
  const p = countHits(text, POSITIVE);
  const neg = countHits(text, NEGATIVE);
  if (p > 0 && neg > 0) return 'mixed';
  if (p > neg) return 'positive';
  if (neg > p) return 'negative';
  return 'neutral';
}

/** First 2–3 sentences as a simple summary. */
export function summarizeText(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
  const take = parts.slice(0, 3).join(' ');
  return take.length > 320 ? `${take.slice(0, 317)}…` : take;
}

/** Rough bullets from lines or sentence splits. */
export function extractKeyPoints(raw: string): string[] {
  const lines = raw
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length >= 2) {
    return lines.slice(0, 6);
  }
  const sentences = raw
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return sentences.slice(0, 5);
}

export type SummarizeUpdateResult = {
  aiSummary: string;
  sentiment: UpdateSentiment;
  keyPoints: string[];
};

/**
 * Local placeholder — replace with an API call that returns the same shape.
 */
export function summarizeCompanyUpdate(rawText: string): SummarizeUpdateResult {
  const aiSummary = summarizeText(rawText);
  const sentiment = detectSentiment(rawText);
  const keyPoints = extractKeyPoints(rawText);
  return { aiSummary, sentiment, keyPoints };
}
